import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import ParticlesBackground from "../components/Effects/ParticlesBackground";
import "../styles/pages/Register.css";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    birthdate: "",
    country: "",
    gender: "",
    terms: false,
  });

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, error, clearError, registerSuccess, clearRegisterSuccess } =
    useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (registerSuccess) {
      const timer = setTimeout(() => {
        clearRegisterSuccess();
        navigate("/");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [registerSuccess, clearRegisterSuccess, navigate]);

  useEffect(() => {
    const strength = calculatePasswordStrength(formData.password);
    setPasswordStrength(strength);
  }, [formData.password]);

  useEffect(() => {
    if (error || localError) {
      const timer = setTimeout(() => {
        clearError();
        setLocalError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, localError, clearError]);

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length > 5) strength += 1;
    if (password.length > 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return Math.min(strength, 4);
  };

  const getStrengthLabel = (strength: number): string => {
    const labels = ["Muy débil", "Débil", "Moderada", "Fuerte", "Muy fuerte"];
    return labels[strength];
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));

    if (localError) setLocalError(null);
  };

  const validateForm = (): boolean => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.username ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.birthdate ||
      !formData.country ||
      !formData.gender
    ) {
      setLocalError("Completa todos los campos obligatorios");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setLocalError("Las contraseñas no coinciden");
      return false;
    }

    if (formData.password.length < 6) {
      setLocalError("La contraseña debe tener al menos 6 caracteres");
      return false;
    }

    if (!formData.terms) {
      setLocalError("Debes aceptar los términos y condiciones");
      return false;
    }

    // Validar edad
    const birthDate = new Date(formData.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 13) {
      setLocalError("Debes tener al menos 13 años para registrarte");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        password: formData.password,
        birthdate: formData.birthdate,
        country: formData.country,
        gender: formData.gender,
      });
    } catch (err) {
      // El error ya está manejado en el contexto
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-page">
      <ParticlesBackground />

      <main className="register-container">
        <div className="register-card">
          <div className="register-header">
            <i className="fas fa-scroll register-icon"></i>
            <h2>Crear Cuenta</h2>
            <p>Únete a la aventura en Aethelbur Chronicles</p>
          </div>

          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  <i className="fas fa-user"></i> Nombre
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  disabled={isSubmitting || registerSuccess}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  <i className="fas fa-user"></i> Apellido
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Tu apellido"
                  required
                  disabled={isSubmitting || registerSuccess}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="username">
                <i className="fas fa-user-tag"></i> Nombre de Usuario
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Elige un nombre único"
                required
                disabled={isSubmitting || registerSuccess}
              />
              <small className="form-text">
                Este será tu nombre en el juego
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <i className="fas fa-envelope"></i> Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tu@correo.com"
                required
                disabled={isSubmitting || registerSuccess}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">
                  <i className="fas fa-lock"></i> Contraseña
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Crea una contraseña segura"
                  required
                  disabled={isSubmitting || registerSuccess}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">
                  <i className="fas fa-lock"></i> Confirmar
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repite tu contraseña"
                  required
                  disabled={isSubmitting || registerSuccess}
                />
              </div>
            </div>

            <div className="password-strength">
              <span
                className="strength-bar"
                style={{
                  backgroundColor: passwordStrength >= 1 ? "#e74c3c" : "#444",
                }}
              ></span>
              <span
                className="strength-bar"
                style={{
                  backgroundColor: passwordStrength >= 2 ? "#e67e22" : "#444",
                }}
              ></span>
              <span
                className="strength-bar"
                style={{
                  backgroundColor: passwordStrength >= 3 ? "#f1c40f" : "#444",
                }}
              ></span>
              <span
                className="strength-bar"
                style={{
                  backgroundColor: passwordStrength >= 4 ? "#2ecc71" : "#444",
                }}
              ></span>
              <span className="strength-text">
                Seguridad:{" "}
                <span id="strengthLevel">
                  {getStrengthLabel(passwordStrength)}
                </span>
              </span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="birthdate">
                  <i className="fas fa-birthday-cake"></i> Fecha Nacimiento
                </label>
                <input
                  type="date"
                  id="birthdate"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || registerSuccess}
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">
                  <i className="fas fa-globe"></i> País
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="">Selecciona tu país</option>
                  <option value="VE">Venezuela</option>
                  <option value="ES">España</option>
                  <option value="MX">México</option>
                  <option value="AR">Argentina</option>
                  <option value="CO">Colombia</option>
                  <option value="CL">Chile</option>
                  <option value="US">Estados Unidos</option>
                  <option value="FR">Francia</option>
                  <option value="DE">Alemania</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="gender">
                <i className="fas fa-venus-mars"></i> Género
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Seleccionar</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
                <option value="prefer_not_to_say">Prefiero no decirlo</option>
              </select>
            </div>

            <div className="terms">
              <input
                type="checkbox"
                id="terms"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                required
                disabled={isSubmitting || registerSuccess}
              />
              <label htmlFor="terms">
                Acepto los <a href="#">Términos del Reino</a> y la{" "}
                <a href="#">Política de Privacidad</a>. Autorizo el tratamiento
                de mis datos para la creación y gestión de mi cuenta.
              </label>
            </div>

            <button
              type="submit"
              className="btn register-btn"
              disabled={!formData.terms || isSubmitting || registerSuccess}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Creando cuenta...
                </>
              ) : registerSuccess ? (
                <>
                  <i className="fas fa-check"></i> ¡Cuenta creada!
                </>
              ) : (
                "Crear Cuenta"
              )}
            </button>

            {registerSuccess && (
              <div
                className="alert-message success"
                style={{ display: "flex" }}
              >
                <i className="fas fa-check-circle"></i>
                <span>¡Cuenta creada con éxito! Redirigiendo al inicio...</span>
              </div>
            )}

            {(error || localError) && (
              <div className="alert-message error" style={{ display: "flex" }}>
                <i className="fas fa-exclamation-circle"></i>
                <span>{error || localError}</span>
              </div>
            )}
          </form>

          <div className="divider">
            <span>o regístrate con</span>
          </div>

          <div className="social-buttons">
            <button type="button" className="social-btn google">
              <i className="fab fa-google"></i>
            </button>
            <button type="button" className="social-btn facebook">
              <i className="fab fa-facebook-f"></i>
            </button>
            <button type="button" className="social-btn steam">
              <i className="fab fa-steam"></i>
            </button>
          </div>

          <div className="login-redirect">
            ¿Ya tienes una cuenta? <Link to="/">Inicia sesión</Link>
          </div>
        </div>

        <div className="register-features">
          <div className="feature">
            <i className="fas fa-dragon"></i>
            <h3>Crea tu Leyenda</h3>
            <p>
              Forja tu propio destino en el mundo de Aethelbur. Personaliza tu
              personaje y elige tu camino.
            </p>
          </div>
          <div className="feature">
            <i className="fas fa-users"></i>
            <h3>Únete a la Comunidad</h3>
            <p>
              Forma alianzas, crea gremios y compite con otros jugadores en
              eventos épicos.
            </p>
          </div>
          <div className="feature">
            <i className="fas fa-gift"></i>
            <h3>Regalo de Bienvenida</h3>
            <p>
              Recibe 100 de oro, una espada inicial y un paquete de bienvenida
              exclusivo.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Register;
